<template>
  <div class="search-origin">
    <Input
      id="originStation"
      :label="$t('travelPlanner.search.origin')"
      :internalLabel="true"
      v-model="inputValue"
      :hasDelete="true"
      :placeholder="$t('travelPlanner.search.origin')"
      :icon="compass"
      :suggestions="filteredStations"
      @focus="onInputFocus"
      @select-suggestion="handleSelectSuggestion" />
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue'
  import { useTravelSearchStore } from '@/stores/travelSearchStore'
  import { fetchStations } from '@/services/journeyService'
  import { pushInteractionToDatalayer } from '@/services/gaLoggingService'
  import Input from '@/components/BaseComponents/Input.vue'
  import compass from '@/assets/icons/compass.svg'
  import { getSortedStationSuggestions } from '@/utils/stationSortingUtils'
  import { findStation } from '@/utils/stationAgglomeration'
  import { getSearchHistory, addToSearchHistory } from '@/composables/useSearchHistory'

  defineOptions({
    name: 'SearchOrigin',
  })

  const travelSearchStore = useTravelSearchStore()
  const stations = computed(() => travelSearchStore.allStations)
  const inputValue = ref('')

  const onInputFocus = async () => {
    try {
      // Only fetch stations if not already loaded
      if (!stations.value.length) {
        const fetchedStations = await fetchStations()
        travelSearchStore.setAllStations(fetchedStations)
      }
    } catch (error) {
      console.error('Error fetching stations:', error)
    }
  }

  const handleSelectSuggestion = (stationCode: string) => {
    const name =
      findStation(stationCode, stations.value)?.name ??
      getSearchHistory('originSearchHistory').find((h) => h.code === stationCode)?.name
    if (name) {
      travelSearchStore.setOriginStation({ name, _u_i_c_station_code: stationCode })
      const { name: originName, _u_i_c_station_code: code } = travelSearchStore.OriginStation
      travelSearchStore.setOriginDestination(originName, travelSearchStore.destination)
      inputValue.value = originName
      addToSearchHistory('originSearchHistory', { name: originName, code })
      pushInteractionToDatalayer('add origin', { selection: originName })
    }
  }

  const filteredStations = computed(() => {
    if (!inputValue.value) return getSearchHistory('originSearchHistory')
    return getSortedStationSuggestions(stations.value, inputValue.value)
  })

  watch(
    () => travelSearchStore.OriginStation,
    (newOriginStation) => {
      inputValue.value = newOriginStation.name
      travelSearchStore.setOriginDestination(newOriginStation.name, travelSearchStore.destination)
    },
    { immediate: true },
  )

  watch(
    () => inputValue.value,
    (newValue) => {
      if (!newValue) {
        travelSearchStore.setOriginStation({
          name: '',
          _u_i_c_station_code: '',
        })
        travelSearchStore.setOriginDestination('', travelSearchStore.destination)
      }
    },
  )
</script>

<style lang="scss" scoped>
  // Field sizing is centralised in SearchForm via the `search-field-*`
  // tokens/mixins, so no per-field height override is needed here.
  .search-origin {
    display: flex;
    flex-direction: column;
  }
</style>
